import { Button } from 'designSystem/components/Button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from 'designSystem/components/ContextMenu'
import { Input } from 'designSystem/components/Input'
import { Tabs, TabsList, TabsTrigger } from 'designSystem/components/Tabs'
import { Tooltip, TooltipTrigger } from 'designSystem/components/Tooltip'
import { KNOWLEDGE_DOC_TAB_ACTION, useGeckoAnalytics } from 'features/analytics'
import { useChatbotAttachments } from 'features/chatbot'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { createDefaultTab } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { getWorkspaceTabIcon } from 'features/workspaceTabs/getWorkspaceTabIcon'
import { Plus, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RolodexEntryType } from 'types'
import { useDragReorder } from 'utils/useDragReorder'

import {
  DocumentTabTooltipContent,
  TabAddButtonSlot,
  TabCloseButtonSlot,
  TabColorIndicator,
  TabDropIndicator,
  TabEditContainer,
  TabIconSlot,
  TabShell,
  TabTitle,
  TabTitleText,
} from './documentTabsStyles'

const TAB_TITLE_TOOLTIP_DELAY_MS = 500

function DocumentTabTitleTooltip({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const hoverDelayRef = useRef<number | null>(null)

  const clearHoverDelay = useCallback(() => {
    if (hoverDelayRef.current === null) return

    window.clearTimeout(hoverDelayRef.current)
    hoverDelayRef.current = null
  }, [])

  const handleMouseEnter = useCallback(() => {
    clearHoverDelay()
    hoverDelayRef.current = window.setTimeout(() => {
      setIsOpen(true)
      hoverDelayRef.current = null
    }, TAB_TITLE_TOOLTIP_DELAY_MS)
  }, [clearHoverDelay])

  const handleMouseLeave = useCallback(() => {
    clearHoverDelay()
    setIsOpen(false)
  }, [clearHoverDelay])

  useEffect(() => clearHoverDelay, [clearHoverDelay])

  return (
    <Tooltip open={isOpen}>
      <TooltipTrigger asChild>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ width: '100%' }}>
          {children}
        </div>
      </TooltipTrigger>
      <DocumentTabTooltipContent side='bottom' sideOffset={5}>
        {label}
      </DocumentTabTooltipContent>
    </Tooltip>
  )
}

export function DocumentTabs() {
  const {
    openTabs,
    removeTab,
    renameTab,
    reorderTabs,
    activeTabId,
    setActiveTabId,
    addTab,
    tabIndicatorColors,
  } = useKnowledgeDocumentUXContext()
  const { siteID } = useKnowledgeDocumentDataContext()
  const { trackEvent } = useGeckoAnalytics()
  const { removeAttachment } = useChatbotAttachments()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    pipelineConfigCacheScope,
  } = usePipelineConfig()

  const [renamingTabId, setRenamingTabId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const pendingRenameFocusRef = useRef<string | null>(null)
  const renameStartedAtRef = useRef<number>(0)
  const [draggingTabId, setDraggingTabId] = useState<string | null>(null)

  const tabDrag = useDragReorder<DocumentTab>('tab-id')

  const onTabDragStart = useCallback(
    (e: React.DragEvent, tabId: string) => {
      setDraggingTabId(tabId)
      tabDrag.handleDragStart(e, tabId)
    },
    [tabDrag]
  )

  const onTabDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      tabDrag.handleDrop(e, targetId)
      const reordered = tabDrag.reorder(openTabs)
      if (reordered) {
        // Pinned tabs (closable === false) always stay left-most, regardless of
        // where a drag dropped things.
        const pinned = reordered.filter((t) => t.closable === false)
        const rest = reordered.filter((t) => t.closable !== false)
        reorderTabs(pinned.length ? [...pinned, ...rest] : reordered)
      }
      setDraggingTabId(null)
    },
    [tabDrag, openTabs, reorderTabs]
  )

  const onTabDragEnd = useCallback(() => {
    tabDrag.handleDragEnd()
    setDraggingTabId(null)
  }, [tabDrag])

  const tabDocumentIds = useMemo(
    () =>
      openTabs
        .map((tab) => {
          const item = tab.history[tab.currentIndex]
          return item.type === 'document' ? item.documentId : null
        })
        .filter((id): id is string => id !== null),
    [openTabs]
  )

  const scopedTabDocumentIds = useMemo(
    () => (isPipelineConfigScopeLoading ? [] : tabDocumentIds),
    [isPipelineConfigScopeLoading, tabDocumentIds]
  )
  const tabDocumentLoadOptions = useMemo(
    () => ({
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: getPipelineConfigScopedTags({ site: siteID }),
    }),
    [getPipelineConfigScopedTags, siteID]
  )
  const { data: tabDocuments } =
    useRolodexEntriesCachedById<SystemDocumentEntry>(
      scopedTabDocumentIds,
      undefined,
      {
        cacheScope: pipelineConfigCacheScope,
        loadOptions: tabDocumentLoadOptions,
      }
    )

  const handleTabClick = (tabId: string) => {
    if (renamingTabId === tabId) return
    const tab = openTabs.find((t) => t.id === tabId)
    if (tab) {
      trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
        action: 'switch',
        tab_id: tabId,
        document_id: getCurrentDocumentFromTab(tab)?.entry.id ?? '',
        total_tabs: openTabs.length,
      })
      setActiveTabId(tabId)
    }
  }

  const handleStartRename = (tabId: string) => {
    const tab = openTabs.find((t) => t.id === tabId)
    if (tab) {
      pendingRenameFocusRef.current = tabId
      renameStartedAtRef.current = performance.now()
      setRenameValue(tab.customLabel ?? getReportName(tab))
      setRenamingTabId(tabId)
    }
  }

  const handleConfirmRename = () => {
    if (renamingTabId) {
      renameTab(renamingTabId, renameValue.trim())
      setRenamingTabId(null)
    }
  }

  const handleRenameBlur = () => {
    const elapsedSinceRenameStart =
      performance.now() - renameStartedAtRef.current
    if (elapsedSinceRenameStart < 250) {
      renameInputRef.current?.focus()
      return
    }

    handleConfirmRename()
  }

  const handleCancelRename = () => {
    setRenamingTabId(null)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirmRename()
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation()
    const closedTab = openTabs.find((t) => t.id === tabId)
    const isClosingActiveTab = activeTabId === tabId
    const closedTabDocId = closedTab
      ? getCurrentDocumentFromTab(closedTab)?.entry.id
      : ''

    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'close',
      tab_id: tabId,
      document_id: closedTabDocId,
      total_tabs: openTabs.length,
      was_active: isClosingActiveTab,
    })

    if (closedTabDocId) {
      removeAttachment(closedTabDocId)
    }

    removeTab(tabId)
  }

  const handleAddTab = useCallback(() => {
    const newTab: DocumentTab = createDefaultTab()
    trackEvent(KNOWLEDGE_DOC_TAB_ACTION.title, {
      action: 'add',
      tab_id: newTab.id,
      total_tabs: openTabs.length + 1,
    })
    addTab(newTab)
  }, [addTab, openTabs.length, trackEvent])

  const getCurrentDocumentFromTab = useCallback(
    (tab: DocumentTab) => {
      const currentItem = tab.history[tab.currentIndex]
      if (currentItem.type === 'document') {
        return tabDocuments?.find(
          (entry) => entry.entry.id === currentItem.documentId
        )
      }
      return undefined
    },
    [tabDocuments]
  )

  const getReportName = useCallback(
    (tab: DocumentTab): string => {
      if (tab.customLabel) return tab.customLabel

      const currentItem = tab.history[tab.currentIndex]

      if (
        currentItem.type === 'search' &&
        currentItem.linkedDocuments !== undefined
      ) {
        return `References to ${currentItem.linkedDocuments.referenceText}`
      }

      if (currentItem.type === 'timeseries') {
        return currentItem.label
      }

      const currentDocument = getCurrentDocumentFromTab(tab)
      if (!currentDocument) return 'New Tab'
      return toTitleCase(
        currentDocument.entry.data.document_title ??
          currentDocument.entry.data.filename ??
          'New Tab'
      )
    },
    [getCurrentDocumentFromTab]
  )

  // Focus input when rename starts
  useEffect(() => {
    if (!renamingTabId) return

    const focusRenameInput = () => {
      const input = renameInputRef.current
      if (!input) return

      input.focus()
      input.select()
      pendingRenameFocusRef.current = null
    }

    const animationFrame = requestAnimationFrame(focusRenameInput)
    const timeout = window.setTimeout(focusRenameInput, 0)

    return () => {
      cancelAnimationFrame(animationFrame)
      window.clearTimeout(timeout)
    }
  }, [renamingTabId])

  return (
    <Tabs value={activeTabId} onValueChange={handleTabClick}>
      <TabsList
        variant='browser'
        justify='start'
        width='full'
        size='sm'
        aria-label='Open documents'>
        {openTabs.map((tab) => {
          const isRenaming = renamingTabId === tab.id
          const isActive = activeTabId === tab.id
          const tabIndicatorColor = tabIndicatorColors.get(tab.id)
          const reportName = getReportName(tab)
          const TabIcon = getWorkspaceTabIcon(tab)

          return (
            <ContextMenu key={tab.id}>
              <ContextMenuTrigger asChild>
                <TabShell
                  $active={isActive}
                  $dragging={draggingTabId === tab.id}
                  $renaming={isRenaming}
                  draggable={renamingTabId !== tab.id && tab.closable !== false}
                  onDragStart={(e) => onTabDragStart(e, tab.id)}
                  onDragOver={(e) => tabDrag.handleDragOver(e, tab.id)}
                  onDragLeave={tabDrag.handleDragLeave}
                  onDrop={(e) => onTabDrop(e, tab.id)}
                  onDragEnd={onTabDragEnd}>
                  {tabDrag.dropTarget?.id === tab.id && (
                    <TabDropIndicator
                      $position={
                        tabDrag.dropTarget.position === 'before'
                          ? 'before'
                          : 'after'
                      }
                    />
                  )}

                  {isRenaming ? (
                    <TabEditContainer>
                      {tabIndicatorColor && (
                        <TabColorIndicator $color={tabIndicatorColor} />
                      )}
                      <TabIconSlot>
                        <TabIcon />
                      </TabIconSlot>
                      <Input
                        ref={renameInputRef}
                        inputSize='inlineEdit'
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        onBlur={handleRenameBlur}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TabEditContainer>
                  ) : (
                    <DocumentTabTitleTooltip label={reportName}>
                      <TabsTrigger
                        variant='browser'
                        size='sm'
                        justify='start'
                        actionArea='inlineEnd'
                        value={tab.id}>
                        {tabIndicatorColor && (
                          <TabColorIndicator $color={tabIndicatorColor} />
                        )}
                        <TabIconSlot>
                          <TabIcon />
                        </TabIconSlot>
                        <TabTitle>
                          <TabTitleText>{reportName}</TabTitleText>
                        </TabTitle>
                      </TabsTrigger>
                    </DocumentTabTitleTooltip>
                  )}

                  {!isRenaming && tab.closable !== false && (
                    <TabCloseButtonSlot>
                      <Button
                        variant='browserTabCloseAction'
                        size='icon-sm'
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => handleCloseTab(e, tab.id)}
                        aria-label='Close tab'>
                        <X />
                      </Button>
                    </TabCloseButtonSlot>
                  )}
                </TabShell>
              </ContextMenuTrigger>
              <ContextMenuContent
                onCloseAutoFocus={(event) => {
                  if (pendingRenameFocusRef.current === tab.id) {
                    event.preventDefault()
                  }
                }}>
                <ContextMenuItem onSelect={() => handleStartRename(tab.id)}>
                  Rename
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          )
        })}
        <TabAddButtonSlot>
          <Button
            variant='browserTabAction'
            size='tab-icon'
            onClick={handleAddTab}
            title='Add new tab'
            aria-label='Add new tab'>
            <Plus />
          </Button>
        </TabAddButtonSlot>
      </TabsList>
    </Tabs>
  )
}
